import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Input, Label, Spinner, Modal, ModalBody } from "reactstrap";

// Página PÚBLICA (fuera del guard de autenticación).
// La reserva solo existe si Stripe confirma el pago; aquí solo iniciamos checkout.

const BASE = process.env.REACT_APP_URL_LUGARES_FIESTAS;
const PRECIO = 1000; // MXN por lugar (solo para mostrar; el monto real lo calcula el servidor)

const SECCIONES = [
  { key: "hombresRH", label: "Hombres — Rosh Hashaná" },
  { key: "mujeresRH", label: "Mujeres — Rosh Hashaná" },
  { key: "mujeresRHA", label: "Mujeres Afuera — Rosh Hashaná" },
  { key: "hombresKipur", label: "Hombres — Yom Kipur" },
  { key: "mujeresKipur", label: "Mujeres — Yom Kipur" },
  { key: "mujeresKipurA", label: "Mujeres Afuera — Yom Kipur" },
];

const fmtMXN = (v) => "$" + new Intl.NumberFormat("es-MX").format(v) + " MXN";
const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// ── Reglas de cobranza (espejo del servidor, solo para mostrar el total) ──────
//   · "Mujeres Afuera": gratis para todos
//   · Abrej: hombres gratis, mujeres $1,000
//   · No Abrej: hombres y mujeres $1,000
const SECCIONES_GRATIS = ["mujeresRHA", "mujeresKipurA"];
const SECCIONES_HOMBRES = ["hombresRH", "hombresKipur"];

// Registro abierto/cerrado (espejo del servidor, que además rechaza el checkout).
// En false se oculta el formulario y se muestra el aviso de cierre.
const REGISTRO_ABIERTO = false;

// Secciones cerradas al público (espejo del servidor, que además las rechaza).
// Se siguen mostrando en la lista, pero deshabilitadas.
const SECCIONES_CERRADAS = ["hombresRH", "mujeresRHA"];

// Cupón que deja la reserva sin costo (la validación real es del servidor)
const CUPON_CODE = "puraberaja";

const precioSeccion = (seccion, esAbrej) => {
  if (SECCIONES_GRATIS.includes(seccion)) return 0;
  if (esAbrej && SECCIONES_HOMBRES.includes(seccion)) return 0;
  return PRECIO;
};

function ReservaPublica() {
  const [disp, setDisp] = useState({}); // seccion -> {disponibles, limite, ocupados}
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [esAbrej, setEsAbrej] = useState(false);
  const [comentarios, setComentarios] = useState("");
  const [cupon, setCupon] = useState("");
  const [cant, setCant] = useState(
    SECCIONES.reduce((a, s) => ({ ...a, [s.key]: 0 }), {})
  );

  // Modal de confirmación (al volver de Stripe o al reservar sin costo)
  const [confirmacion, setConfirmacion] = useState(null);

  // Deja el formulario en blanco
  const limpiarFormulario = () => {
    setNombre(""); setEmail(""); setTelefono("");
    setEsAbrej(false); setComentarios(""); setCupon("");
    setCant(SECCIONES.reduce((a, s) => ({ ...a, [s.key]: 0 }), {}));
  };

  const cargarDisponibilidad = () => {
    setLoading(true);
    axios
      .get(`${BASE}/disponibilidad`)
      .then((r) => {
        const map = {};
        (r.data || []).forEach((c) => (map[c.seccion] = c));
        setDisp(map);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(cargarDisponibilidad, []); // eslint-disable-line

  // Al volver de Stripe: ?session_id=... → traer el resumen y mostrar el modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    // Limpia la URL para que un refresh no vuelva a abrir el modal
    window.history.replaceState({}, "", window.location.pathname);
    axios
      .get(`${BASE}/confirmacion/${sessionId}`)
      .then((r) => {
        // Volvió de Stripe sin completar el pago: no hay reserva ni modal
        if (r.data && r.data.pendientePago) {
          cargarDisponibilidad();
          Swal.fire({
            icon: "info",
            title: "No se completó el pago",
            text: "Tu reserva no quedó registrada. Puedes intentarlo de nuevo cuando quieras.",
          });
          return;
        }
        setConfirmacion(r.data);
        limpiarFormulario();
        cargarDisponibilidad();
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const totalLugares = useMemo(
    () => SECCIONES.reduce((s, sec) => s + (Number(cant[sec.key]) || 0), 0),
    [cant]
  );
  const cuponValido = cupon.trim().toLowerCase() === CUPON_CODE;

  // Total según las reglas de cobranza por sección (el cupón lo deja en 0)
  const totalMXN = useMemo(
    () => cuponValido
      ? 0
      : SECCIONES.reduce((t, sec) => t + (Number(cant[sec.key]) || 0) * precioSeccion(sec.key, esAbrej), 0),
    [cant, esAbrej, cuponValido]
  );
  const esGratis = totalMXN === 0;

  const nombreOk = nombre.trim().length > 0;
  const telOk = telefono.trim().length >= 8 && telefono.trim().length <= 20;
  const puedeEnviar = totalLugares > 0 && nombreOk && emailOk(email) && telOk && !submitting;

  // Qué falta para habilitar el botón (para dar pista al usuario)
  const faltantes = [];
  if (!nombreOk) faltantes.push("nombre");
  if (!emailOk(email)) faltantes.push("un correo válido");
  if (!telOk) faltantes.push("teléfono de 8 a 20 dígitos");
  if (totalLugares < 1) faltantes.push("al menos un lugar");

  const setCantidad = (key, val) => {
    if (SECCIONES_CERRADAS.includes(key)) return;
    const disponibles = disp[key] ? disp[key].disponibles : 0;
    let n = parseInt(val, 10);
    if (isNaN(n) || n < 0) n = 0;
    if (n > disponibles) n = disponibles;
    setCant((c) => ({ ...c, [key]: n }));
  };

  const enviar = async () => {
    if (!puedeEnviar) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BASE}/checkout`, {
        nombre: nombre.trim(),
        email: email.trim(),
        telefono: telefono.trim(),
        esAbrej,
        comentarios: comentarios.trim(),
        cupon: cupon.trim(),
        lugares: cant,
      });
      if (data.gratis) {
        // Sin costo: ya quedó confirmada, mostramos el modal aquí mismo
        setSubmitting(false);
        setConfirmacion(data.reserva);
        limpiarFormulario();
        cargarDisponibilidad();
        return;
      }
      // Con costo: redirige a Stripe (al volver, el useEffect abre el modal)
      window.location = data.url;
    } catch (err) {
      setSubmitting(false);
      if (err.response && err.response.status === 409) {
        Swal.fire({
          icon: "warning",
          title: "Ya no hay lugares",
          text: "Una de las secciones se agotó mientras completabas el formulario. Actualizamos la disponibilidad.",
        });
        cargarDisponibilidad();
      } else {
        Swal.fire({ icon: "error", title: "No se pudo iniciar el pago", text: "Intenta de nuevo en unos momentos." });
      }
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", padding: "24px 12px" }}>
      <div style={{ maxWidth: 560, margin: "0 auto", background: "#fff", borderRadius: 16,
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)", overflow: "hidden" }}>

        {/* Encabezado */}
        <div style={{ background: "#0f172a", padding: "22px 20px" }}>
          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 800, color: "#ffffff" }}>Reserva de Lugares Shaare Beraja</h2>

          <div style={{ marginTop: 12, fontSize: "0.8rem", lineHeight: 1.55 }}>
            <p style={{ margin: 0, color: "#ffffff" }}>
              Con gran agradecimiento a Hashem, contamos con un Midrash lleno de Torá y Talmide Jajamim,
              gracias a su constante asistencia y apoyo.
            </p>

            <p style={{ margin: "10px 0 4px", color: "#ffffff", fontWeight: 700 }}>
              El costo de recuperación de los lugares es de:
            </p>
            <ul style={{ margin: "0 0 10px", paddingLeft: 18, color: "#ffffff" }}>
              <li style={{ color: "#ffffff" }}>Rosh Hashaná: <b style={{ color: "#ffffff" }}>{fmtMXN(PRECIO)}</b> por persona</li>
              <li style={{ color: "#ffffff" }}>Yom Kipur: <b style={{ color: "#ffffff" }}>{fmtMXN(PRECIO)}</b> por persona</li>
            </ul>

            <p style={{ margin: "0 0 8px", color: "#ffffff" }}>
              Estos montos están destinados al Kolel y son completamente deducibles de Maaser.
            </p>
            <p style={{ margin: "0 0 8px", color: "#ffffff" }}>
              El corazón de nuestro lugar son los Abrejim, por lo que no se cobrará el lugar a los hombres únicamente.
            </p>
            <p style={{ margin: "0 0 8px", color: "#ffffff" }}>
              Si alguien presenta dificultades de pago, les pedimos acercarse a alguno de los Gabaim para hacer una excepción.
            </p>
            <p style={{ margin: "0 0 8px", color: "#ffffff" }}>
              Finalmente, les solicitamos de favor apartar únicamente los lugares que realmente vayan a ocupar,
              ya que Baruj Hashem contamos con mucha asistencia.
            </p>
            <p style={{ margin: "0 0 10px", color: "#fca5a5", fontWeight: 700 }}>
              No se puede reservar a menores de 10 años.
            </p>

            <p style={{ margin: 0, textAlign: "center", color: "#ffffff", fontWeight: 700, fontSize: "0.95rem" }}
               dir="rtl" lang="he">
              תזכו לשנים רבות נעימות בטובות
            </p>
          </div>
        </div>

        {!REGISTRO_ABIERTO ? (
          /* Registro cerrado: se conserva el encabezado con la leyenda y se
             sustituye el formulario por el aviso. */
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em",
              textTransform: "uppercase", color: "#dc2626", marginBottom: 8 }}>
              Registro cerrado
            </div>
            <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
              El registro de lugares ya cerró, favor de contactar a los Gabaim.
            </div>
            <p style={{ margin: "0 auto", maxWidth: 380, fontSize: "0.85rem", color: "#64748b", lineHeight: 1.5 }}>
              Gracias a todos los que apartaron su lugar. Si tienes alguna duda sobre tu
              reserva, acércate con alguno de los Gabaim.
            </p>
          </div>
        ) : (
        <div style={{ padding: "20px" }}>
          {/* Contacto */}
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <Label style={lbl}>Nombre completo *</Label>
              <Input bsSize="sm" value={nombre} onChange={(e) => setNombre(e.target.value)} invalid={!!nombre && !nombreOk} />
            </div>
            <div>
              <Label style={lbl}>Correo *</Label>
              <Input bsSize="sm" type="email" value={email} onChange={(e) => setEmail(e.target.value)} invalid={!!email && !emailOk(email)} />
            </div>
            <div>
              <Label style={lbl}>Teléfono *</Label>
              <Input bsSize="sm" value={telefono} onChange={(e) => setTelefono(e.target.value)} invalid={!!telefono && !telOk} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input id="abrej" type="checkbox" checked={esAbrej} onChange={(e) => setEsAbrej(e.target.checked)} />
              <Label for="abrej" style={{ ...lbl, margin: 0 }}>¿Es Abrej?</Label>
            </div>
          </div>

          {/* Secciones */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 700, fontSize: "0.8rem", color: "#475569", textTransform: "uppercase",
              letterSpacing: "0.05em", marginBottom: 8 }}>Selecciona tus lugares</div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "24px 0" }}><Spinner size="sm" /> Cargando disponibilidad…</div>
            ) : (
              SECCIONES.map((sec) => {
                const d = disp[sec.key];
                const disponibles = d ? d.disponibles : 0;
                const cerrada = SECCIONES_CERRADAS.includes(sec.key);
                const agotado = disponibles <= 0;
                const bloqueada = cerrada || agotado;
                const precio = precioSeccion(sec.key, esAbrej);
                return (
                  <div key={sec.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 0", borderBottom: "1px solid #f1f5f9", opacity: bloqueada ? 0.55 : 1 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#0f172a" }}>{sec.label}</div>
                      <div style={{ fontSize: "0.72rem", color: bloqueada ? "#dc2626" : "#64748b" }}>
                        {cerrada ? "Cerrado" : agotado ? "Agotado" : `${disponibles} disponibles`}
                        {!cerrada && (
                          <>
                            {" · "}
                            <span style={{ color: precio === 0 ? "#16a34a" : "#64748b", fontWeight: precio === 0 ? 700 : 400 }}>
                              {precio === 0 ? "Gratis" : fmtMXN(precio)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <Input bsSize="sm" type="number" min="0" max={cerrada ? 0 : disponibles} disabled={bloqueada}
                      value={cerrada ? 0 : cant[sec.key]} onChange={(e) => setCantidad(sec.key, e.target.value)}
                      style={{ width: 76, textAlign: "center" }} />
                  </div>
                );
              })
            )}
          </div>

          {/* Comentarios */}
          <div style={{ marginTop: 16 }}>
            <Label style={lbl}>Comentarios (opcional)</Label>
            <Input bsSize="sm" type="textarea" rows={2} value={comentarios} onChange={(e) => setComentarios(e.target.value)} />
          </div>

          {/* Cupón */}
          {/* <div style={{ marginTop: 16 }}>
            <Label style={lbl}>Cupón (opcional)</Label>
            <Input bsSize="sm" value={cupon} placeholder="Escribe tu cupón"
              onChange={(e) => { const v = e.target.value; setCupon(v); }}
              style={cuponValido ? { borderColor: "#16a34a" } : undefined} />
            {cupon.trim() !== "" && (
              <div style={{ marginTop: 4, fontSize: "0.72rem", fontWeight: 600,
                color: cuponValido ? "#16a34a" : "#d97706" }}>
                {cuponValido ? "✓ Cupón aplicado · tu reserva no será cargada a la TDC" : "Cupón no válido"}
              </div>
            )}
          </div> */}

          {/* Total + enviar */}
          <div style={{ marginTop: 20, padding: "16px", background: "#f8fafc", borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
              <div style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total</div>
              <div style={{ fontSize: "0.74rem", color: "#64748b" }}>
                {totalLugares} lugar{totalLugares === 1 ? "" : "es"}{esGratis && totalLugares > 0 ? (cuponValido ? " · Cupón aplicado" : " · Sin costo") : ""}
              </div>
            </div>
            <div style={{ fontSize: "1.7rem", fontWeight: 800, lineHeight: 1.1, marginTop: 2, whiteSpace: "nowrap",
              color: esGratis ? "#16a34a" : "#0f172a" }}>{fmtMXN(totalMXN)}</div>
            {!puedeEnviar && !submitting && faltantes.length > 0 && (
              <div style={{ marginTop: 12, padding: "9px 12px", background: "#fffbeb",
                border: "1px solid #fde68a", borderRadius: 8,
                fontSize: "0.75rem", color: "#b45309", fontWeight: 600, textAlign: "center" }}>
                Para continuar, falta: {faltantes.join(", ")}.
              </div>
            )}

            <button onClick={enviar} disabled={!puedeEnviar}
              style={{ marginTop: 14, width: "100%", border: "none", borderRadius: 10, padding: "15px",
                fontWeight: 700, fontSize: "1.02rem", color: "#fff",
                background: puedeEnviar ? "#10b981" : "#cbd5e1",
                cursor: puedeEnviar ? "pointer" : "not-allowed" }}>
              {submitting ? "Enviando…" : esGratis ? "Reservar (gratis)" : "Pagar y reservar"}
            </button>
          </div>

          {/* <p style={{ marginTop: 12, fontSize: "0.72rem", color: "#94a3b8", textAlign: "center" }}>
            {cuponValido
              ? "Con el cupón tu reserva queda registrada sin costo, pendiente de confirmación."
              : esGratis
                ? "Tu reserva es sin costo y queda confirmada al enviar."
                : "El pago se procesa de forma segura con Stripe. Tu lugar se aparta 30 minutos mientras completas el pago."}
          </p> */}
        </div>
        )}
      </div>

      {/* ── Modal de confirmación: lugares registrados ─────────────────────── */}
      <Modal isOpen={!!confirmacion} toggle={() => setConfirmacion(null)}>
        <ModalBody style={{ padding: "30px 26px", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#dcfce7",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>

          <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, color: "#0f172a" }}>
            {confirmacion && confirmacion.estado === "pendiente" ? "¡Reserva registrada!" : "¡Reserva confirmada!"}
          </h3>
          <p style={{ margin: "8px 0 0", fontSize: "0.85rem", color: "#475569" }}>
            {confirmacion && confirmacion.estado === "pendiente"
              ? "Guardamos tus lugares."
              : "Te enviamos el detalle por correo."}
          </p>

          {confirmacion && (
            <>
              {confirmacion.folio && (
                <div style={{ marginTop: 12, fontSize: "0.75rem", color: "#64748b" }}>
                  Folio <b style={{ color: "#0f172a" }}>#{confirmacion.folio}</b>
                </div>
              )}

              <div style={{ marginTop: 18, textAlign: "left", background: "#f8fafc",
                borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#64748b",
                  textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                  Lugares registrados
                </div>
                {SECCIONES.filter((s) => (confirmacion.lugares || {})[s.key] > 0).map((s) => (
                  <div key={s.key} style={{ display: "flex", justifyContent: "space-between",
                    padding: "5px 0", fontSize: "0.82rem", color: "#334155" }}>
                    <span>{s.label}</span>
                    <b style={{ color: "#0f172a" }}>{confirmacion.lugares[s.key]}</b>
                  </div>
                ))}
                <div style={{ borderTop: "1px solid #e2e8f0", marginTop: 8, paddingTop: 8,
                  display: "flex", justifyContent: "space-between", fontSize: "0.85rem", fontWeight: 800 }}>
                  <span style={{ color: "#475569" }}>
                    Total · {confirmacion.totalLugares} lugar{confirmacion.totalLugares === 1 ? "" : "es"}
                  </span>
                  <span style={{ color: "#16a34a" }}>
                    {fmtMXN((confirmacion.totalCentavos || 0) / 100)}
                  </span>
                </div>
              </div>

              {confirmacion.estado === "pendiente" && (
                <p style={{ marginTop: 12, fontSize: "0.72rem", color: "#d97706" }}>
                  {confirmacion.conCupon
                    ? "Tu reserva quedó registrada con el cupón y está pendiente de confirmación."
                    : "Estamos confirmando tu pago. En cuanto se acredite recibirás el correo."}
                </p>
              )}
            </>
          )}

          <button onClick={() => setConfirmacion(null)}
            style={{ marginTop: 20, width: "100%", border: "none", borderRadius: 10, padding: "13px",
              fontWeight: 700, fontSize: "0.95rem", color: "#fff", background: "#10b981", cursor: "pointer" }}>
            Cerrar
          </button>
        </ModalBody>
      </Modal>
    </div>
  );
}

const lbl = { fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 3, display: "block" };

export default ReservaPublica;
